import { Box, Typography } from '@mui/material';
import type { ChatCitation, ChatMessageStatus, ChatRole } from '@hv/shared';
import { BRAND } from '../theme';
import { CitedText } from './CitedText';
import { UnverifiedQuoteText } from './UnverifiedQuotes';

export interface ChatBubbleProps {
  role: ChatRole;
  text: string;
  status?: ChatMessageStatus;
  unverifiedSpans?: readonly string[];
  citations?: ChatCitation[];
  onCite?: (c: ChatCitation) => void;
}

const ignoreCite = () => {};

export function ChatBubble({ role, text, status = 'ok', unverifiedSpans = [], citations = [], onCite = ignoreCite }: ChatBubbleProps) {
  const mine = role === 'user';
  const uncited = !mine && citations.length === 0 && status !== 'refused' && status !== 'error';
  const cited = (plain: string) => <CitedText text={plain} citations={citations} onCite={onCite} />;
  return (
    <Box data-testid="chat-message" data-role={role} data-status={status} sx={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', mb: 1.5 }}>
      <Typography
        component="div"
        variant="body2"
        data-testid="chat-text"
        sx={{
          maxWidth: '85%',
          px: 1.5,
          py: 1,
          borderRadius: 2,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          lineHeight: 1.55,
          bgcolor: mine ? BRAND.navy : '#f1f4f8',
          color: mine ? '#fff' : 'text.primary',
          border: mine ? 'none' : `1px solid ${BRAND.line}`,
        }}
      >
        {mine ? text : <UnverifiedQuoteText text={text} spans={unverifiedSpans} renderPlain={cited} />}
      </Typography>
      {uncited && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, ml: 0.5 }}>
          No source cited
        </Typography>
      )}
      <AnswerNotes status={status} unverified={unverifiedSpans.length} />
    </Box>
  );
}

function AnswerNotes({ status, unverified }: { status: ChatMessageStatus; unverified: number }) {
  const notes = [
    status === 'truncated' && 'Answer cut off',
    unverified > 0 && `${unverified === 1 ? 'A quote' : `${unverified} quotes`} marked unverified ${unverified === 1 ? 'was' : 'were'} not found in this household's record`,
  ].filter(Boolean);
  if (notes.length === 0) return null;
  return (
    <Typography variant="caption" color="text.secondary" data-testid="chat-answer-notes" sx={{ mt: 0.5, maxWidth: '85%' }}>
      {notes.join(' · ')}
    </Typography>
  );
}
